import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import api, { getFreshAccessToken } from "../../../chat/api";
import toast from "react-hot-toast";

const pdfImg = "/pdff.svg";
const xlsImg = "/Xls.svg";
const docImg = "/docX.svg";

const UploadModal = ({ isOpen, onClose, file, onRemove, folderId, onUploadSuccess }) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("idle"); // idle, uploading, success, error
  const [errorMessage, setErrorMessage] = useState(null);
  
  const wsRef = useRef(null);
  
  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
      setProgress(0);
      setStatus("idle");
      setErrorMessage(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Helper from your reference component
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    else return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  const getFileExtension = (filename) => {
    return filename?.split(".").pop()?.toLowerCase() || "";
  };

  const getFileIcon = (filename) => {
    const ext = getFileExtension(filename);
    if (ext === "pdf") {
      return <img src={pdfImg} alt="PDF" className="w-8 h-10 flex-shrink-0" />;
    } else if (ext === "xls" || ext === "xlsx" || ext === "csv") {
      return <img src={xlsImg} alt="Excel" className="w-8 h-10 flex-shrink-0" />;
    } else if (ext === "doc" || ext === "docx") {
      return <img src={docImg} alt="Word" className="w-8 h-10 flex-shrink-0" />;
    } else {
      return <img src={docImg} alt="Document" className="w-8 h-10 flex-shrink-0" />;
    }
  };

  const handleUpload = async () => {
    if (!file || !folderId) return;

    setStatus("uploading");
    setProgress(0);
    setErrorMessage(null);

    try {
      const token = await getFreshAccessToken();

      // Step 1: Open WebSocket connection
      const wsUrl = process.env.REACT_APP_WS_URL || "ws://localhost:8000";
      const ws = new WebSocket(`${wsUrl}/ws/upload-status/?token=${token}`);
      wsRef.current = ws;

      ws.onmessage = (e) => {
        const data = JSON.parse(e.data);
        console.log("WebSocket message:", data);

        if (data.type === "progress") {
          setProgress(data.progress);
        } else if (data.type === "completed") {
          setStatus("success");
          setProgress(100);
          
          toast.success("File extracted successfully!");
          
          // Close after 2 seconds
          setTimeout(() => {
            if (onUploadSuccess) onUploadSuccess();
            onClose();
          }, 2000);
          
          ws.close();
        } else if (data.type === "error") {
          setStatus("error");
          setErrorMessage(data.message || "Upload failed");
          ws.close();
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setStatus("error");
        setErrorMessage("WebSocket connection failed");
      };

      // Step 2: Once WS is open, trigger API call
      ws.onopen = async () => {
        console.log("WebSocket connected, uploading file...");
        try {
          const formData = new FormData();
          formData.append("files", file);

          await api.post(
            `/api/v1/rfp/folders/${folderId}/upload/?questionaire=true`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: (ev) => {
                // Initial network upload progress (0-30%) before backend processing
                const percent = Math.round((ev.loaded * 100) / ev.total);
                setProgress((prev) => Math.max(prev, Math.min(percent, 30)));
              },
            }
          );
        } catch (error) {
          console.error("API Upload error:", error);
          setStatus("error");
          setErrorMessage(error.response?.data?.error || error.response?.data?.message || "Upload failed");
          if (ws.readyState === WebSocket.OPEN) {
            ws.close();
          }
        }
      };

    } catch (error) {
      console.error("Setup error:", error);
      setStatus("error");
      setErrorMessage("Failed to initiate upload");
    }
  };

  const getButtonText = () => {
    if (status === "uploading") return "Processing...";
    if (status === "success") return "Extracted";
    return "Upload and Extract";
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden font-outfit">
        {/* Header */}
        <div className="px-4 pt-4 pb-2 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-[#18101C]">
            Selected File {file ? "(1)" : "(0)"}
          </h2>
        </div>

        {/* Content */}
        <div className="px-4 py-2 min-h-[100px]">
          {file ? (
            <div className="flex flex-col px-4 pt-3 border border-gray-200 rounded-xl bg-white mb-3 relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* File Icon */}
                  {getFileIcon(file.name)}
                  
                  {/* File Details */}
                  <div className="flex flex-col">
                    <span className="text-[#18101C] font-medium text-[15px] truncate max-w-[300px]">
                      {file.name}
                    </span>
                    <span className="text-[#71717A] text-[13px]">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  onClick={onRemove}
                  disabled={status === "uploading" || status === "success"}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Progress Bar - MATCHING STYLE EXACTLY */}
              {(status === "uploading" || status === "success" || status === "error") && (
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ease-linear ${
                      status === "error"
                        ? "bg-red-500"
                        : status === "success"
                        ? "bg-green-500"
                        : "bg-purple-600"
                    }`}
                    style={{
                      width: `${progress}%`,
                      background:
                        status === "uploading"
                          ? "linear-gradient(180deg, #C95EBE 0%, #8B55DD 100%)"
                          : undefined,
                    }}
                  ></div>
                </div>
              )}
              
              {/* Status Text or Error */}
              <div className="mt-1 flex justify-between items-center min-h-[20px]">
                {status === "uploading" }
                {status === "error" && (
                  <span className="text-xs text-red-500 font-medium">
                    {errorMessage}
                  </span>
                )}
                {status === "success" && (
                  <span className="text-xs text-green-500 font-medium ml-auto">
                    Completed
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-400 py-8">
              No file selected. Please close and try again.
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-4 pt-2 pb-4 flex justify-center items-center gap-4">
          <button
            onClick={onClose}
            disabled={status === "uploading" || status === "success"}
            className="px-8 py-2.5 rounded-xl border border-[#C95EBE] text-[#C95EBE] font-medium hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || status === "uploading" || status === "success"}
            className={`px-8 py-2.5 rounded-xl font-medium text-white transition-colors ${
              file && status !== "uploading"
                ? status === "success" 
                  ? "bg-green-500 hover:bg-green-600 shadow-md"
                  : "bg-[#C95EBE] hover:bg-[#b04da6] shadow-md"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
